import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { InstallPWAButton } from './InstallPWAButton';
import { useDevice } from '../../hooks/useDevice';
import { usePWAInstall } from '../../hooks/usePWAInstall';

interface FirstVisitBannerProps {
  config: any;
  onClose: () => void;
  forceShow?: boolean;
  platform?: 'ios' | 'android';
}

export const FirstVisitBanner: React.FC<FirstVisitBannerProps> = ({
  config,
  onClose,
  forceShow = false,
  platform
}) => {
  const { i18n } = useTranslation();
  const [shouldShow, setShouldShow] = useState(false);
  const { isDesktop } = useDevice();
  const { hasNativePrompt } = usePWAInstall();

  useEffect(() => {
    if (isDesktop) {
      setShouldShow(false);
      return;
    }

    const hasVisited = localStorage.getItem('has_visited');
    if (!hasVisited || forceShow) {
      setShouldShow(true);
      if (!hasVisited) {
        localStorage.setItem('has_visited', 'true');
      }
    }
  }, [forceShow, isDesktop]);

  if (!shouldShow || isDesktop) {
    return null;
  }

  const handleClose = () => {
    setShouldShow(false);
    onClose();
    localStorage.setItem('has_visited', 'true');
  };

  const ShareIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="28" height="28" className="text-white">
      <path fill="currentColor" d="M35.27 15.84h-4.1a1.5 1.5 0 0 0 0 3h4.1a1.5 1.5 0 0 1 1.5 1.5v19.62a1.5 1.5 0 0 1-1.5 1.5H12.72a1.5 1.5 0 0 1-1.5-1.5V20.34a1.5 1.5 0 0 1 1.5-1.5h4.1a1.5 1.5 0 0 0 0-3h-4.1a4.5 4.5 0 0 0-4.5 4.5v19.62a4.5 4.5 0 0 0 4.5 4.5h22.55a4.5 4.5 0 0 0 4.5-4.5V20.34a4.5 4.5 0 0 0-4.5-4.5zm-16.87-3.03 4.14-4.14v19.75a1.5 1.5 0 0 0 3 0V8.67l4.14 4.14a1.5 1.5 0 1 0 2.12-2.12l-6.7-6.7a1.5 1.5 0 0 0-2.12 0l-6.7 6.7a1.5 1.5 0 0 0 2.12 2.12z"/>
    </svg>
  );

  const AddIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" className="text-white">
      <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
    </svg>
  );

  const MenuIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" className="text-white">
      <path fill="currentColor" d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
    </svg>
  );

  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';
  const showManualAndroid = isAndroid && !hasNativePrompt;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[400] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-sm mx-auto relative overflow-hidden"
        style={{ borderRadius: config.border_radius }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
          style={{ color: config['dark-color'] }}
        >
          <X size={20} />
        </button>

        <div className="p-6">
          {isIOS ? (
            <>
              <h2 className="text-xl font-semibold mb-2" style={{ color: config['dark-color'] }}>
                Installer l'application
              </h2>
              <p className="text-gray-600 mb-4">
                Installez l'application sur votre appareil pour un accès rapide et une meilleure expérience.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: config['major-color'] }}
                  >
                    <ShareIcon />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">1. Appuyez sur Partager</h3>
                    <p className="text-sm text-gray-600">Touchez l'icône de partage en bas de l'écran</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: config['major-color'] }}
                  >
                    <AddIcon />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">2. Ajouter à l'écran d'accueil</h3>
                    <p className="text-sm text-gray-600">Sélectionnez "Sur l'écran d'accueil"</p>
                  </div>
                </div>
              </div>
            </>
          ) : showManualAndroid ? (
            <>
              <h2 className="text-xl font-semibold mb-2" style={{ color: config['dark-color'] }}>
                Installer l'application
              </h2>
              <p className="text-gray-600 mb-4">
                Installez l'application sur votre appareil pour un accès rapide et une meilleure expérience.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: config['major-color'] }}
                  >
                    <MenuIcon />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">1. Ouvrez le menu du navigateur</h3>
                    <p className="text-sm text-gray-600">Appuyez sur les 3 points en haut à droite</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: config['major-color'] }}
                  >
                    <AddIcon />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">2. Ajouter à l'écran d'accueil</h3>
                    <p className="text-sm text-gray-600">Sélectionnez "Ajouter à l'écran d'accueil"</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center">
              <InstallPWAButton
                config={config}
                variant="banner"
                platform={platform}
                onInstalled={handleClose}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
